/* SPATIALx RADIO — shuffle-rotation player for the fixed top bar.
   Markup: sections/spatialx-radio-bar.liquid (inline playlist JSON in a
   type="application/json" script tag). Appearance: assets/spatialx-radio.css.

   Unified site-wide audio exclusivity contract (shared with
   spatialx-card-actions.js and downconverter.js): every audio surface
   dispatches window 'sx:audio-play' with detail.source when it starts,
   and pauses itself on anyone else's dispatch — only one broadcast at a
   time, whichever surface it comes from.

   Playback survives page navigation via sessionStorage: the next page
   restores the same track and position and attempts to resume. If the
   browser blocks the gesture-less play(), the bar waits in the paused
   state with the track loaded — one tap continues the broadcast.

   The ticker is rendered as HTML rather than plain text so the ON AIR
   (red) and UP NEXT (amber) tags can be colour-coded. Everything
   interpolated into it comes from the playlist JSON, but it is escaped
   on the way in regardless — the blob is generated from merchant-entered
   product titles, so it is treated as untrusted.

   The audio element, playlist and queue live at module scope; the DOM
   refs re-bind on every init so the theme editor's section re-renders
   (shopify:section:load) keep the bar alive. */
(function () {
  var STORE = 'sx-radio-v1';
  var ERROR_BUDGET = 5;

  var ui = null; // { root, toggleBtn, skipBtn, tickerEl, trackEl, runs }
  var playlist = null;
  var upcoming = []; // playlist indices still to play; [0] is always the true "up next"
  var current = null;
  var audio = null;
  var lastSave = 0;
  var consecutiveErrors = 0;
  // Seek target restored from sessionStorage but not yet applied (the
  // element only loads metadata once it actually starts fetching). Kept
  // out of the element so a src change can invalidate it.
  var pendingSeek = null;
  var pendingSeekSrc = null;
  // True when a gesture-less resume after a navigation was refused by the
  // browser's autoplay policy. The track is loaded and seeked; only the
  // start is missing. Kept distinct from a deliberate pause so the bar can
  // ask for the tap rather than sitting there claiming to be ON AIR.
  var awaitingResume = false;

  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  function esc(value) {
    return String(value).replace(/[&<>"']/g, function (ch) { return ESCAPES[ch]; });
  }

  function parsePlaylist() {
    if (playlist) return playlist;
    var el = document.querySelector('[data-sx-radio-playlist]');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      playlist = data.tracks.map(function (row) {
        return { title: row[0], artist: row[1], cat: row[2], handle: row[3], src: data.base + row[4] };
      });
    } catch (e) {
      playlist = null;
    }
    return playlist;
  }

  // Refill the upcoming queue with a fresh shuffle of every track index.
  // avoidFirst: index that must not come up immediately (the track that
  // just played), so a reshuffle never repeats back-to-back.
  function refillQueue(avoidFirst) {
    var list = parsePlaylist();
    if (!list || !list.length) return false;
    var idx = list.map(function (_, i) { return i; });
    for (var i = idx.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = idx[i];
      idx[i] = idx[j];
      idx[j] = tmp;
    }
    if (idx.length > 1 && avoidFirst != null && idx[0] === avoidFirst) {
      var k = 1 + Math.floor(Math.random() * (idx.length - 1));
      var t2 = idx[0];
      idx[0] = idx[k];
      idx[k] = t2;
    }
    upcoming = idx;
    return true;
  }

  function currentIndex() {
    var list = parsePlaylist();
    if (!list || !current) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === current.handle) return i;
    }
    return null;
  }

  function isPlayingUI() {
    return !!(audio && !audio.paused && !audio.error);
  }

  function syncPlayingClass(isPlaying) {
    if (!ui) return;
    ui.root.classList.toggle('sx-radio--playing', !!isPlaying);
    ui.toggleBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    ui.toggleBtn.setAttribute('aria-label', isPlaying ? 'Pause SPATIALx Radio' : 'Play SPATIALx Radio');
  }

  function getAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';

    audio.addEventListener('play', function () {
      syncPlayingClass(true);
      // Unified site-wide exclusivity contract: every audio surface (radio,
      // card previews, the PDP downconverter) dispatches 'sx:audio-play'
      // with its source when it starts, and pauses itself on anyone else's.
      try { window.dispatchEvent(new CustomEvent('sx:audio-play', { detail: { source: 'radio' } })); } catch (e) {}
      save(true);
    });

    // 'playing' (not 'play') is evidence sound is actually coming out —
    // only then is the error streak really over. Resetting on 'play'
    // would let a cascade of sources that die mid-load defeat the
    // circuit breaker.
    audio.addEventListener('playing', function () {
      consecutiveErrors = 0;
      // A refused resume that later succeeds must drop the PAUSED billing.
      if (awaitingResume) {
        awaitingResume = false;
        if (current) announce(current);
      }
      setPlaybackState('playing');
    });

    audio.addEventListener('pause', function () {
      syncPlayingClass(false);
      setPlaybackState('paused');
      save(false);
    });

    audio.addEventListener('ended', function () {
      next(true);
    });

    audio.addEventListener('error', function () {
      // A dead stream shouldn't stall the station — move on, but never
      // spin forever if every source is failing (offline, CDN outage).
      if (!current) return;
      consecutiveErrors += 1;
      if (consecutiveErrors < ERROR_BUDGET) {
        next(true);
      } else {
        try { audio.pause(); } catch (e) {}
        syncPlayingClass(false);
        setTicker(
          '<span class="sx-radio__tag sx-radio__tag--lost">SIGNAL LOST</span>' +
          '<span class="sx-radio__now">Check your connection and press play</span>',
          'SIGNAL LOST Check your connection and press play',
          false
        );
        save(false);
      }
    });

    audio.addEventListener('loadedmetadata', function () {
      if (pendingSeek != null && pendingSeekSrc && audio.src === pendingSeekSrc) {
        try { audio.currentTime = pendingSeek; } catch (e) {}
      }
      pendingSeek = null;
      pendingSeekSrc = null;
    });

    audio.addEventListener('timeupdate', function () {
      var now = Date.now();
      if (now - lastSave > 4000) {
        lastSave = now;
        save(!audio.paused);
      }
    });

    return audio;
  }

  function upNext() {
    var list = parsePlaylist();
    if (!list || !upcoming.length) return null;
    return list[upcoming[0]] || null;
  }

  // html: already-escaped markup for one marquee run.
  // plain: the same content as flat text, used only to scale the scroll
  // duration so long and short billings read at the same pace.
  function setTicker(html, plain, linkable) {
    if (!ui) return;
    for (var i = 0; i < ui.runs.length; i++) ui.runs[i].innerHTML = html;
    var secs = Math.max(22, Math.min(75, Math.round(plain.length * 0.42)));
    ui.trackEl.style.setProperty('--sx-radio-speed', secs + 's');
    // Restart the marquee from the seam so new text enters cleanly.
    ui.trackEl.style.animation = 'none';
    void ui.trackEl.offsetWidth;
    ui.trackEl.style.animation = '';
    ui.root.classList.toggle('sx-radio--linkable', !!linkable);
  }

  function announce(entry) {
    var nowText = entry.title + ' — ' + entry.artist;
    var html =
      '<span class="sx-radio__tag sx-radio__tag--live">ON AIR</span>' +
      '<span class="sx-radio__now">' + esc(nowText) + '</span>' +
      '<span class="sx-radio__cat">' + esc(entry.cat) + '</span>';
    var plain = 'ON AIR ' + nowText + ' ' + entry.cat;

    var nxt = upNext();
    if (nxt) {
      var nextText = nxt.title + ' — ' + nxt.artist;
      html +=
        '<span class="sx-radio__tag sx-radio__tag--next">UP NEXT</span>' +
        '<span class="sx-radio__soon">' + esc(nextText) + '</span>';
      plain += ' UP NEXT ' + nextText;
    }

    setTicker(html, plain, true);
  }

  // Shown only after a blocked auto-resume. Deliberately not linkable: the
  // one thing to do here is press play, and a ticker click would navigate
  // away instead.
  function announceResume(entry) {
    var nowText = entry.title + ' \u2014 ' + entry.artist;
    var html =
      '<span class="sx-radio__tag sx-radio__tag--resume">PAUSED</span>' +
      '<span class="sx-radio__now">Press play to pick up where you left off</span>' +
      '<span class="sx-radio__cat">' + esc(nowText) + '</span>';
    setTicker(html, 'PAUSED Press play to pick up where you left off ' + nowText, false);
  }

  // Lock screen, notification shade and hardware media keys. Artwork is
  // omitted: the playlist blob carries no cover URLs, so adding it means
  // regenerating that snapshot rather than changing anything here.
  function updateMediaSession(entry) {
    if (!('mediaSession' in navigator) || typeof window.MediaMetadata !== 'function') return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: entry.title,
        artist: entry.artist,
        album: 'SPATIALx Radio \u00b7 ' + entry.cat
      });
    } catch (e) {}
  }

  function bindMediaSession() {
    if (!('mediaSession' in navigator)) return;
    var handlers = {
      play: function () { onToggleClick(); },
      pause: function () { if (audio && !audio.paused) audio.pause(); },
      nexttrack: function () { consecutiveErrors = 0; next(true); }
    };
    for (var action in handlers) {
      try { navigator.mediaSession.setActionHandler(action, handlers[action]); } catch (e) {}
    }
  }

  function setPlaybackState(state) {
    if (!('mediaSession' in navigator)) return;
    try { navigator.mediaSession.playbackState = state; } catch (e) {}
  }

  // fromRestore: this play() carries no user gesture, so the browser may
  // refuse it. A refusal is not an error — the track stays loaded at the
  // right position — but the bar has to say so instead of lying.
  function attemptPlay(fromRestore) {
    var a = getAudio();
    // After a SIGNAL LOST stop the element is stuck in its error state;
    // re-assigning the source is what actually re-runs the load.
    if (a.error && current) {
      pendingSeek = null;
      pendingSeekSrc = null;
      a.src = current.src;
    }
    if (!fromRestore) {
      // A user tap out of the PAUSED billing must put the ON AIR ticker
      // back straight away — the 'playing' handler below can't do it,
      // because the flag it checks is about to be cleared right here.
      if (awaitingResume && current) announce(current);
      awaitingResume = false;
    }
    var p = a.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        if (!fromRestore || !current) return;
        awaitingResume = true;
        announceResume(current);
        syncPlayingClass(false);
      });
    }
  }

  function setTrack(entry, autoplay) {
    var a = getAudio();
    pendingSeek = null;
    pendingSeekSrc = null;
    current = entry;
    a.src = entry.src;
    announce(entry);
    updateMediaSession(entry);
    save(!!autoplay);
    if (autoplay) attemptPlay();
  }

  function next(autoplay) {
    var list = parsePlaylist();
    if (!list || !list.length) return;
    if (!upcoming.length && !refillQueue(currentIndex())) return;
    var idx = upcoming.shift();
    // Refill eagerly when the queue empties so upNext() always has a
    // truthful answer for the ticker, never a stale pre-reshuffle peek.
    if (!upcoming.length) refillQueue(idx);
    setTrack(list[idx], autoplay);
  }

  function save(playing) {
    if (!current) return;
    try {
      sessionStorage.setItem(STORE, JSON.stringify({
        entry: current,
        // If a restored seek hasn't been applied yet, the element still
        // reads 0 — persist the pending target instead so a second
        // navigation doesn't wipe the real position.
        time: pendingSeek != null ? pendingSeek : (audio ? audio.currentTime || 0 : 0),
        // A refused resume still means "the listener wants this playing" —
        // persist that so the next page tries again rather than giving up.
        playing: !!playing || awaitingResume
      }));
    } catch (e) {}
  }

  function restore() {
    var raw = null;
    try { raw = sessionStorage.getItem(STORE); } catch (e) {}
    if (!raw) return;
    var st;
    try { st = JSON.parse(raw); } catch (e) { return; }
    if (!st || !st.entry || !st.entry.src) return;

    var a = getAudio();
    current = st.entry;
    a.src = st.entry.src;
    if (st.time) {
      pendingSeek = st.time;
      pendingSeekSrc = st.entry.src;
    }
    refillQueue(currentIndex());
    announce(st.entry);
    updateMediaSession(st.entry);
    if (st.playing) attemptPlay(true);
  }

  function onToggleClick() {
    var a = getAudio();
    consecutiveErrors = 0;
    if (current) {
      if (a.paused || a.error) {
        if (a.error) announce(current); // leaving SIGNAL LOST — restore the ticker
        attemptPlay();
      } else {
        a.pause();
      }
      return;
    }
    next(true);
  }

  function onSkipClick() {
    consecutiveErrors = 0;
    next(true);
  }

  function onTickerClick() {
    if (!ui || !ui.root.classList.contains('sx-radio--linkable')) return;
    if (current && current.handle) {
      window.location.href = '/products/' + current.handle;
    }
  }

  // Idempotent per-DOM-instance init. The theme editor re-renders the
  // section on every settings change (shopify:section:load) and the
  // re-inserted markup arrives with no listeners — so everything DOM-
  // bound lives here and re-binds against the fresh nodes.
  function initRadio() {
    var root = document.getElementById('sx-radio');
    if (!root || root.dataset.sxRadioBound === 'true') return;

    var toggleBtn = root.querySelector('[data-sx-radio-toggle]');
    var skipBtn = root.querySelector('[data-sx-radio-skip]');
    var tickerEl = root.querySelector('[data-sx-radio-ticker]');
    var trackEl = root.querySelector('[data-sx-radio-track]');
    var runs = root.querySelectorAll('[data-sx-radio-run]');
    if (!toggleBtn || !skipBtn || !tickerEl || !trackEl || !runs.length) return;

    root.dataset.sxRadioBound = 'true';
    ui = { root: root, toggleBtn: toggleBtn, skipBtn: skipBtn, tickerEl: tickerEl, trackEl: trackEl, runs: runs };

    toggleBtn.addEventListener('click', onToggleClick);
    skipBtn.addEventListener('click', onSkipClick);
    tickerEl.addEventListener('click', onTickerClick);

    // Re-paint state onto the fresh DOM (no-op on very first load).
    if (current) {
      announce(current);
      syncPlayingClass(isPlayingUI());
    }
  }

  window.addEventListener('sx:audio-play', function (e) {
    if (e.detail && e.detail.source === 'radio') return;
    if (audio && !audio.paused) audio.pause();
  });

  window.addEventListener('pagehide', function () {
    if (current) save(audio ? !audio.paused : false);
  });

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#sx-radio')) initRadio();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#sx-radio')) {
      if (audio && !audio.paused) audio.pause();
      ui = null;
    }
  });

  bindMediaSession();
  initRadio();
  restore();
})();
